import React from 'react';

import { Tooltip } from '@mui/material';
import { DateTime } from 'luxon';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate } from '@clearverify/utils';

import { type IntegrationUsageLogInfoFragment, IntegrationUsageLogStatus } from 'src/api';
import { Table, type TableColumn, type TableFilter } from 'src/components/table';
import {
  INTEGRATION_USAGE_LOG_ENDPOINT_FILTER_OPTIONS,
  INTEGRATION_USAGE_LOG_ENDPOINT_LABELS,
  INTEGRATION_USAGE_LOG_FILTER_IDS,
  INTEGRATION_USAGE_LOG_STATUS_FILTER_OPTIONS,
  INTEGRATION_USAGE_LOG_STATUS_LABELS,
} from 'src/constants/integrationUsageLog';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import type { PaginationContextValue } from 'src/providers/PaginationProvider';

type IntegrationUsageLogsTableProps = {
  usageLogsList: IntegrationUsageLogInfoFragment[];
  usageLogsCount: number;
  onRefresh?: () => Promise<void>;
  pagination: PaginationContextValue;
  loading: boolean;
};

const formatDuration = (ms?: number | null): string => {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

export const IntegrationUsageLogsTable: React.FC<IntegrationUsageLogsTableProps> = ({
  usageLogsList,
  usageLogsCount,
  onRefresh,
  pagination,
  loading,
}) => {
  const { setSearchValue, searchValue } = useSearch();
  const { setSortOption } = useFilterStorage();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  return (
    <Table
      dataSource={usageLogsList}
      rowCount={usageLogsCount}
      columns={columns}
      onSearch={setSearchValue}
      onSort={setSortOption}
      pagination={pagination}
      filters={filters}
      onRefresh={onRefresh}
      loading={loading}
    />
  );
};

const columns: Array<TableColumn<IntegrationUsageLogInfoFragment>> = [
  {
    title: 'Requested At',
    key: 'createdAt',
    sortable: true,
    nonNullSortable: true,
    render: (_value, log) => {
      const fullTimestamp = formatDate(log.createdAt, DateFormatPatterns.fullDateWithTime);

      return <span title={log.createdAt}>{fullTimestamp}</span>;
    },
  },
  {
    title: 'Duration',
    key: 'duration',
    sortable: true,
    render: (_value, log) => formatDuration(log.duration),
  },
  {
    title: 'Endpoint',
    key: 'endpoint',
    render: (_value, log) =>
      (INTEGRATION_USAGE_LOG_ENDPOINT_LABELS as Record<string, string>)[log.endpoint] ??
      log.endpoint,
  },
  {
    title: 'Request ID',
    key: 'requestId',
    render: (_value, log) => log.requestId,
  },
  {
    title: 'Status',
    key: 'status',
    render: (_value, log) => {
      const label = INTEGRATION_USAGE_LOG_STATUS_LABELS[log.status];
      if (log.status === IntegrationUsageLogStatus.Failed && log.errorMessage) {
        return (
          <Tooltip title={log.errorMessage}>
            <span>{label}</span>
          </Tooltip>
        );
      }
      return label;
    },
  },
  {
    title: 'Verified',
    key: 'verified',
    sortable: true,
    render: (_value, log) => log.verified,
  },
  {
    title: 'Ambiguous match',
    key: 'ambiguous',
    sortable: true,
    render: (_value, log) => log.ambiguous,
  },
  {
    title: 'No Match',
    key: 'noMatch',
    sortable: true,
    render: (_value, log) => log.noMatch,
  },
  {
    title: 'HTTP Code',
    key: 'statusCode',
    render: (_value, log) => log.statusCode ?? '-',
  },
  {
    title: 'Requester ID',
    key: 'requesterId',
    render: (_value, log) => log.requesterId ?? '-',
  },
  {
    title: 'Last 4 SSN',
    key: 'last4Ssn',
    render: (_value, log) => log.last4Ssn ?? '-',
  },
  {
    title: 'DOB',
    key: 'dateOfBirth',
    render: (_value, log) =>
      log.dateOfBirth ? formatDate(log.dateOfBirth, DateFormatPatterns.shortDateWithSlash) : '-',
  },
];

const filters: TableFilter[] = [
  {
    id: INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtFrom,
    type: 'date',
    fieldProps: {
      label: 'From',
      mayHaveEmptyValue: true,
      maxDate: DateTime.now(),
    },
    gridItemSize: 6,
  },
  {
    id: INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtTo,
    type: 'date',
    fieldProps: {
      label: 'To',
      mayHaveEmptyValue: true,
      maxDate: DateTime.now(),
    },
    gridItemSize: 6,
  },
  {
    id: INTEGRATION_USAGE_LOG_FILTER_IDS.endpoint,
    type: 'select',
    fieldProps: {
      label: 'Endpoint',
      multiple: true,
      options: INTEGRATION_USAGE_LOG_ENDPOINT_FILTER_OPTIONS,
    },
  },
  {
    id: INTEGRATION_USAGE_LOG_FILTER_IDS.status,
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: INTEGRATION_USAGE_LOG_STATUS_FILTER_OPTIONS,
    },
  },
];
